import type { DocumentBlock, DocumentKind } from "@/engine/types";
import { newId } from "@/store/tree-ops";
import { DOCUMENT_KINDS } from "./doc-model";

/**
 * 제목 아래 한 줄. 목록이면 빈 항목, 할 일이면 체크하지 않은 항목.
 * `table`이 있으면 머리글 행과 예시 행 하나의 표다. 글은 `{값}`으로 적는다.
 */
export type DocTemplateSection = {
  title: string;
  list?: boolean;
  table?: string[];
  example?: string[];
  note?: string;
  todos?: string[];
};

/** 워크스페이스가 고친 목차. 시스템 기본값과 id를 공유하지 않는다. */
export type WorkspaceDocTemplate = {
  id: string;
  name: string;
  kind: DocumentKind;
  sections: DocTemplateSection[];
  blocks?: DocumentBlock[];
  updatedAt: string;
  updatedBy?: string;
  createdAt?: string;
  createdBy?: string;
};

export type DocTemplateOrigin = "system" | "workspace";

export type DocTemplateOffer = {
  id: string;
  name: string;
  kind: DocumentKind;
  origin: DocTemplateOrigin;
  sections: DocTemplateSection[];
  blocks?: DocumentBlock[];
  updatedAt?: string;
  updatedBy?: string;
  createdAt?: string;
  createdBy?: string;
};

export type WorkspaceDocTemplateDraft = {
  id?: string;
  name: string;
  kind: DocumentKind;
  sections: readonly DocTemplateSection[];
  blocks?: readonly DocumentBlock[];
  updatedBy?: string;
  createdAt?: string;
  createdBy?: string;
};

const NAME_MAX = 80;
const TITLE_MAX = 80;
const SECTION_MAX = 24;
const COLUMN_MAX = 6;
const COLUMN_TITLE_MAX = 40;
const NOTE_MAX = 80;
const TODO_MAX = 6;

/** 시스템 목차. 문서 종류와 상관없이 같은 셋을 보여 준다. */
const SYSTEM_DOC_TEMPLATES: readonly { id: string; name: string; kind: DocumentKind; sections: readonly DocTemplateSection[] }[] = [
  {
    id: "sys-material",
    name: "자료",
    kind: "material",
    sections: [
      { title: "이력", table: ["날짜", "내용", "작성자"], example: ["{2025/12/23}", "{최초 작성}", "{이름}"] },
      { title: "개요", note: "{누가, 무엇을 위한 문서인지}" },
      { title: "할 일", todos: ["{환경 준비}", "{권한 확인}", "{첫 실행}"] },
    ],
  },
  {
    id: "sys-meeting",
    name: "회의록",
    kind: "meeting",
    sections: [
      { title: "이력", table: ["날짜", "내용", "작성자"], example: ["{2025/12/23}", "{최초 작성}", "{이름}"] },
      { title: "참석", table: ["이름", "소속", "역할"], example: ["{이름}", "{소속}", "{참석}"] },
      { title: "안건", note: "{이번 회의에서 정할 것}" },
      { title: "결정", note: "{정한 내용}" },
      { title: "할 일", todos: ["{담당과 할 일}", "{기한}", "{공유할 사람}"] },
    ],
  },
  {
    id: "sys-memo",
    name: "메모",
    kind: "memo",
    sections: [
      { title: "메모", note: "{메모}" },
      { title: "할 일", todos: ["{할 일}", "{다음에 볼 것}"] },
    ],
  },
];

export function systemDocTemplateOffers(): DocTemplateOffer[] {
  return SYSTEM_DOC_TEMPLATES.map((item) => ({
    id: item.id,
    name: item.name,
    kind: item.kind,
    origin: "system",
    sections: item.sections.map((section) => ({ ...section })),
  }));
}

function workspaceOffers(workspace: readonly WorkspaceDocTemplate[]): DocTemplateOffer[] {
  return workspace.map((item) => ({
    id: item.id,
    name: item.name,
    kind: item.kind,
    origin: "workspace" as const,
    sections: item.sections.map((section) => ({ ...section })),
    ...(item.blocks ? { blocks: item.blocks.map((b) => ({ ...b })) } : {}),
    updatedAt: item.updatedAt,
    updatedBy: item.updatedBy,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
  }));
}

/** 편집 화면. 종류와 관계없이 기본 셋 다음에 워크스페이스 목차다. */
export function editorTemplateOffers(workspace: readonly WorkspaceDocTemplate[] = []): DocTemplateOffer[] {
  return [...systemDocTemplateOffers(), ...workspaceOffers(workspace)];
}

/** 기본 목차가 먼저이고, 그 뒤에 이 워크스페이스 목차다. */
export function docTemplateOffers(kind: DocumentKind, workspace: readonly WorkspaceDocTemplate[] = []): DocTemplateOffer[] {
  return [
    ...systemDocTemplateOffers().filter((item) => item.kind === kind),
    ...workspaceOffers(workspace).filter((item) => item.kind === kind),
  ];
}

export function docTemplateHint(offer: DocTemplateOffer): string {
  const where = offer.origin === "system" ? "기본" : offer.updatedBy ? `워크스페이스 (${offer.updatedBy})` : "워크스페이스";
  return `${where} · ${offer.sections.map((section) => section.title).join(", ")}`;
}

function cleanColumns(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const columns: string[] = [];
  for (const item of raw) {
    if (columns.length >= COLUMN_MAX) break;
    if (typeof item !== "string") continue;
    const text = item.trim().slice(0, COLUMN_TITLE_MAX);
    if (text) columns.push(text);
  }
  return columns.length >= 2 ? columns : undefined;
}

/** `{값}`만 남긴다. 중괄호가 없으면 버린다. */
function braced(raw: unknown, max: number): string | undefined {
  if (typeof raw !== "string") return undefined;
  const match = raw.trim().slice(0, max).match(/^\{([^{}]+)\}$/);
  const inner = match?.[1]?.trim();
  return inner ? `{${inner}}` : undefined;
}

function cleanTodos(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const todos: string[] = [];
  for (const item of raw) {
    if (todos.length >= TODO_MAX) break;
    const text = braced(item, NOTE_MAX);
    if (text) todos.push(text);
  }
  return todos.length ? todos : undefined;
}

/** 열과 같은 자리의 예시. `{2025/12/23}`처럼 중괄호로 감싼 값만 남긴다. */
function cleanExamples(raw: unknown, count: number): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const examples: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const item = raw[i];
    const text = typeof item === "string" ? item.trim().slice(0, COLUMN_TITLE_MAX) : "";
    const match = text.match(/^\{([^{}]+)\}$/);
    examples.push(match?.[1] ? `{${match[1].trim()}}` : "");
  }
  return examples.some(Boolean) ? examples : undefined;
}

export function cleanDocTemplateSections(sections: readonly DocTemplateSection[]): DocTemplateSection[] {
  const out: DocTemplateSection[] = [];
  for (const section of sections) {
    if (out.length >= SECTION_MAX) break;
    const title = section.title.trim().slice(0, TITLE_MAX);
    if (!title) continue;
    const table = cleanColumns(section.table);
    const note = braced(section.note, NOTE_MAX);
    const todos = table ? undefined : cleanTodos(section.todos);
    const extra = {
      ...(note ? { note } : {}),
      ...(todos ? { todos } : {}),
    };
    if (table) {
      const example = cleanExamples(section.example, table.length);
      out.push({ title, table, ...(example ? { example } : {}), ...(note ? { note } : {}) });
    } else if (todos) out.push({ title, ...extra });
    else out.push({ title, ...extra, ...(section.list ? { list: true } : {}) });
  }
  return out;
}

function tableCell(text: string) {
  return {
    type: "tableCell",
    content: text ? [{ type: "text", text, styles: {} }] : [],
    props: { colspan: 1, rowspan: 1 },
  };
}

/** 머리글 한 줄과 예시 행 하나. 예시는 `{값}`이고, 없는 칸은 비운다. */
function revisionTable(id: string, columns: readonly string[], examples: readonly string[] = []): DocumentBlock {
  return {
    id,
    type: "table",
    content: {
      type: "tableContent",
      headerRows: 1,
      rows: [
        { cells: columns.map((column) => tableCell(column)) },
        { cells: columns.map((_, index) => tableCell(examples[index] ?? "")) },
      ],
    },
  };
}

function textBlock(id: string, type: string, text: string, props?: Record<string, unknown>): DocumentBlock {
  return {
    id,
    type,
    ...(props ? { props } : {}),
    content: text ? [{ type: "text", text, styles: {} }] : [],
  };
}

export function blocksFromSections(sections: readonly DocTemplateSection[], nextId: () => string = () => newId("bk")): DocumentBlock[] {
  const blocks: DocumentBlock[] = [];
  for (const section of cleanDocTemplateSections(sections)) {
    blocks.push(textBlock(nextId(), "heading", section.title, { level: 1 }));
    if (section.note) blocks.push(textBlock(nextId(), "paragraph", section.note));
    if (section.table) {
      blocks.push(revisionTable(nextId(), section.table, section.example ?? []));
      continue;
    }
    if (section.todos?.length) {
      for (const todo of section.todos) blocks.push(textBlock(nextId(), "checkListItem", todo, { checked: false }));
      continue;
    }
    if (!section.note || section.list) {
      blocks.push(textBlock(nextId(), section.list ? "bulletListItem" : "paragraph", ""));
    }
  }
  return blocks;
}

function coerceSection(raw: unknown): DocTemplateSection {
  if (!raw || typeof raw !== "object") return { title: "" };
  const row = raw as { title?: unknown; list?: unknown; table?: unknown; example?: unknown; note?: unknown; todos?: unknown };
  const table = cleanColumns(row.table);
  const example = table ? cleanExamples(row.example, table.length) : undefined;
  const note = braced(row.note, NOTE_MAX);
  const todos = table ? undefined : cleanTodos(row.todos);
  return {
    title: typeof row.title === "string" ? row.title : "",
    ...(table ? { table } : {}),
    ...(example ? { example } : {}),
    ...(note ? { note } : {}),
    ...(todos ? { todos } : {}),
    ...(!table && !todos && row.list === true ? { list: true } : {}),
  };
}

export function normalizeWorkspaceDocTemplates(raw: unknown): WorkspaceDocTemplate[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: WorkspaceDocTemplate[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Partial<WorkspaceDocTemplate>;
    if (typeof row.id !== "string" || !row.id || row.id.startsWith("sys-") || seen.has(row.id)) continue;
    if (typeof row.kind !== "string" || !DOCUMENT_KINDS.includes(row.kind as DocumentKind)) continue;
    const name = typeof row.name === "string" ? row.name.trim().slice(0, NAME_MAX) : "";
    const blocks = Array.isArray(row.blocks) ? (row.blocks as DocumentBlock[]) : undefined;
    const sections = cleanDocTemplateSections(
      Array.isArray(row.sections) && row.sections.length > 0
        ? row.sections.map(coerceSection)
        : blocks
          ? sectionsFromBlocks(blocks)
          : [],
    );
    if (!name || !sections.length) continue;
    seen.add(row.id);
    const updatedAt = typeof row.updatedAt === "string" && row.updatedAt ? row.updatedAt : new Date(0).toISOString();
    const updatedBy = typeof row.updatedBy === "string" && row.updatedBy ? row.updatedBy : undefined;
    const createdAt = typeof row.createdAt === "string" && row.createdAt ? row.createdAt : (row.updatedAt ? row.updatedAt : undefined);
    const createdBy = typeof row.createdBy === "string" && row.createdBy ? row.createdBy : updatedBy;
    out.push({
      id: row.id,
      name,
      kind: row.kind,
      sections,
      ...(blocks ? { blocks } : {}),
      updatedAt,
      ...(updatedBy ? { updatedBy } : {}),
      ...(createdAt ? { createdAt } : {}),
      ...(createdBy ? { createdBy } : {}),
    });
  }
  return out;
}

export function upsertWorkspaceDocTemplate(
  list: readonly WorkspaceDocTemplate[],
  input: WorkspaceDocTemplateDraft,
  now: string,
  idFactory: () => string = () => newId("dt"),
  updatedBy?: string,
): WorkspaceDocTemplate[] | null {
  const name = input.name.trim().slice(0, NAME_MAX);
  const blocks = input.blocks ? cloneBlocksWithNewIds(input.blocks) : undefined;
  const sections = cleanDocTemplateSections(
    input.sections && input.sections.length > 0
      ? input.sections
      : blocks
        ? sectionsFromBlocks(blocks)
        : [],
  );
  if (!name || !sections.length) return null;
  if (!DOCUMENT_KINDS.includes(input.kind)) return null;
  const requested = input.id?.trim() ?? "";
  if (requested.startsWith("sys-")) return null;
  const id = requested || idFactory();
  if (!id || id.startsWith("sys-")) return null;
  const author = input.updatedBy ?? updatedBy;
  const existing = list.find((item) => item.id === id);
  const next: WorkspaceDocTemplate = {
    id,
    name,
    kind: input.kind,
    sections,
    ...(blocks ? { blocks } : existing?.blocks ? { blocks: existing.blocks } : {}),
    updatedAt: now,
    ...(author ? { updatedBy: author } : existing?.updatedBy ? { updatedBy: existing.updatedBy } : {}),
    createdAt: existing?.createdAt ?? input.createdAt ?? now,
    ...(existing?.createdBy ? { createdBy: existing.createdBy } : (input.createdBy || author) ? { createdBy: input.createdBy || author } : {}),
  };
  const index = list.findIndex((item) => item.id === id);
  if (index < 0) return [...list, next];
  return list.map((item, i) => (i === index ? next : item));
}

export function removeWorkspaceDocTemplate(list: readonly WorkspaceDocTemplate[], id: string): WorkspaceDocTemplate[] | null {
  if (!id || id.startsWith("sys-") || !list.some((item) => item.id === id)) return null;
  return list.filter((item) => item.id !== id);
}

function extractInlineText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  let text = "";
  for (const item of content) {
    if (!item || typeof item !== "object") continue;
    const row = item as { text?: unknown; content?: unknown };
    if (typeof row.text === "string") {
      text += row.text;
    } else if (Array.isArray(row.content)) {
      text += extractInlineText(row.content);
    }
  }
  return text;
}

function extractTableCellText(cell: unknown): string {
  if (!cell || typeof cell !== "object") return "";
  const c = cell as { content?: unknown };
  return extractInlineText(c.content).trim();
}

/** 문서 블록들로부터 템플릿 목차(섹션)를 추출한다. */
export function sectionsFromBlocks(blocks: readonly DocumentBlock[]): DocTemplateSection[] {
  if (!blocks || !blocks.length) return [];
  const sections: DocTemplateSection[] = [];
  let current: DocTemplateSection | null = null;

  const ensureSection = (): DocTemplateSection => {
    if (!current) {
      current = { title: "개요" };
      sections.push(current);
    }
    return current;
  };

  const ensureBraced = (text: string): string => {
    const inner = text.replace(/[{}]/g, "").trim();
    if (!inner) return "";
    return `{${inner}}`;
  };

  for (const block of blocks) {
    if (!block || !block.type) continue;
    if (block.type === "heading") {
      const title = extractInlineText(block.content).trim();
      current = { title: title || "섹션" };
      sections.push(current);
      continue;
    }

    if (block.type === "table") {
      const sec = ensureSection();
      const content = block.content as { rows?: Array<{ cells?: unknown[] }> } | undefined;
      const rows = content?.rows;
      if (Array.isArray(rows) && rows.length > 0) {
        const headerCells = Array.isArray(rows[0]?.cells) ? rows[0]!.cells : [];
        const columns = headerCells
          .map(extractTableCellText)
          .map((c) => c.slice(0, COLUMN_TITLE_MAX))
          .filter(Boolean);
        if (columns.length >= 2) {
          sec.table = columns.slice(0, COLUMN_MAX);
          if (rows.length > 1) {
            const dataCells = Array.isArray(rows[1]?.cells) ? rows[1]!.cells : [];
            const examples = sec.table.map((_, idx) => {
              const text = extractTableCellText(dataCells[idx]);
              return text ? ensureBraced(text).slice(0, COLUMN_TITLE_MAX) : "";
            });
            if (examples.some(Boolean)) {
              sec.example = examples;
            }
          }
        }
      }
      continue;
    }

    if (block.type === "checkListItem") {
      const sec = ensureSection();
      const text = extractInlineText(block.content).trim();
      if (text) {
        const bracedTodo = ensureBraced(text).slice(0, NOTE_MAX);
        if (bracedTodo) {
          sec.todos = [...(sec.todos ?? []), bracedTodo].slice(0, TODO_MAX);
        }
      }
      continue;
    }

    if (block.type === "bulletListItem" || block.type === "numberedListItem") {
      const sec = ensureSection();
      if (!sec.table && !sec.todos?.length) {
        sec.list = true;
      }
      continue;
    }

    if (block.type === "paragraph" || block.type === "quote" || block.type === "codeBlock") {
      const text = extractInlineText(block.content).trim();
      if (text) {
        const sec = ensureSection();
        const inner = text.replace(/[{}]/g, "").trim();
        if (inner) {
          if (!sec.note) {
            sec.note = `{${inner.slice(0, Math.max(0, NOTE_MAX - 2))}}`;
          } else {
            const existing = sec.note.slice(1, -1);
            const combined = `${existing} / ${inner}`.slice(0, Math.max(0, NOTE_MAX - 2));
            sec.note = `{${combined}}`;
          }
        }
      }
      continue;
    }
  }

  if (sections.length === 0 && blocks.length > 0) {
    sections.push({ title: "개요" });
  }

  return cleanDocTemplateSections(sections);
}

/** 블록 복제 시 고유한 새 id를 부여한다 */
export function cloneBlocksWithNewIds(blocks: readonly DocumentBlock[], idGen: () => string = () => newId("bk")): DocumentBlock[] {
  return blocks.map((block) => ({
    ...block,
    id: idGen(),
    ...(Array.isArray(block.children) ? { children: cloneBlocksWithNewIds(block.children, idGen) } : {}),
  }));
}

/** 템플릿의 블록들을 반환한다. 자체 블록이 있으면 고유 id로 복제하고, 없으면 sections로부터 생성한다. */
export function blocksFromOffer(offer: DocTemplateOffer, idGen: () => string = () => newId("bk")): DocumentBlock[] {
  if (offer.blocks && offer.blocks.length > 0) {
    return cloneBlocksWithNewIds(offer.blocks, idGen);
  }
  return blocksFromSections(offer.sections, idGen);
}

/** 템플릿 복제 드래프트 생성 유틸 */
export function cloneWorkspaceDocTemplate(offer: DocTemplateOffer | WorkspaceDocTemplate, customName?: string): WorkspaceDocTemplateDraft {
  return {
    name: (customName ?? `${offer.name} 복사`).slice(0, NAME_MAX),
    kind: offer.kind,
    sections: offer.sections.map((section) => ({
      title: section.title,
      ...(section.table ? { table: [...section.table] } : {}),
      ...(section.example ? { example: [...section.example] } : {}),
      ...(section.note ? { note: section.note } : {}),
      ...(section.todos ? { todos: [...section.todos] } : {}),
      ...(section.list ? { list: true } : {}),
    })),
    ...(offer.blocks ? { blocks: cloneBlocksWithNewIds(offer.blocks) } : {}),
  };
}

/** 기본 목차를 복사해 고칠 수 있는 초안으로 만든다. id는 비우고 이름만 붙인다. */
export function cloneSystemTemplate(offer: Pick<DocTemplateOffer, "name" | "kind" | "sections">, customName?: string): WorkspaceDocTemplateDraft {
  return cloneWorkspaceDocTemplate(offer as DocTemplateOffer, customName);
}
